import React, { useState, useEffect, useMemo } from "react"
import API from "../../../utils/API"
import "./AiTargeting.css"
import Sweetalert from "../../../utils/Sweetalert"
import ButtonCollapse from "../../ButtonCollapse"
import ButtonRefresh from "../../ButtonRefresh"
import LoadingCard from "../../LoadingCard"
import DiffusionAiTargetingAdItem from "./DiffusionAiTargetingAdItem"
import DiffusionTotemCardTotem from "../DiffusionTotemCardTotem"
import AiTargetingSummaryMatrix from "./AiTargetingSummaryMatrix"

export default function DiffusionAiTargetingCard({ totemSelected, aiTargetingAds, isAiTargetingAdsLoaded, onAdDeleted, onAdUpdated, onRefreshAds, onBack }) {
  const [validationStatus, setValidationStatus] = useState(null)

  const validateConfig = (totemId) => {
    API.GET(`/totem-ai-targeting-ad/totem/${totemId}/validate`, (response) => {
      setValidationStatus(response.data)
    })
  }

  useEffect(() => {
    if (totemSelected?.id && isAiTargetingAdsLoaded) {
      validateConfig(totemSelected.id)
    }
  }, [totemSelected?.id, isAiTargetingAdsLoaded, aiTargetingAds])

  const handleDeleteAd = (adId) => {
    Sweetalert.fireDeleteConfirm(
      "Supprimer la configuration ?",
      "Cette ressource ne sera plus ciblée par l'IA sur ce totem.",
      () => {
        API.DELETE(`/totem-ai-targeting-ad/totem/${totemSelected.id}/ad/${adId}`, (response) => {
          Sweetalert.fireSuccessToast(response.data.message)
          onAdDeleted(adId)
        })
      }
    )
  }

  const defaultAd = aiTargetingAds.find((ad) => ad.isDefault)
  const targetedAds = aiTargetingAds.filter((ad) => !ad.isDefault)

  const otherByAdId = useMemo(() => {
    const map = {}
    targetedAds.forEach((ad) => {
      map[ad.adId] = targetedAds.filter((a) => a.adId !== ad.adId)
    })
    return map
  }, [targetedAds])

  return (
    <div className="card ai-card">
      <div className="card-header">
        <h3 className="card-title">Totem sélectionné</h3>
        <div className="card-tools">
          <ButtonRefresh onClick={onRefreshAds} />
          <ButtonCollapse />
        </div>
      </div>
      <div className="card-body p-0 card-body--block">
        <LoadingCard loaded={isAiTargetingAdsLoaded}>
          <div className="ai-card__totem-header">
            <DiffusionTotemCardTotem totem={totemSelected} isReadOnly={true} />
          </div>

          {validationStatus && !validationStatus.isValid && (
            <div className="ai-card__validation-wrapper">
              <div className="ai-validation-status ai-validation-status--warning">
                <div className="ai-validation-status__icon">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <div className="ai-validation-status__content">
                  <div className="ai-validation-status__title">Configuration incomplète</div>
                  {validationStatus.errors?.length > 0 && (
                    <ul className="ai-validation-status__errors">
                      {validationStatus.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="ai-card__section">
            <div className="ai-section-header ai-section-header--default">
              <div className="ai-section-header__icon-badge ai-section-header__icon-badge--amber">
                <i className="fas fa-star"></i>
              </div>
              <div className="ai-section-header__text">
                <span className="ai-section-header__title">Ressource par défaut</span>
                <span className="ai-section-header__subtitle">Diffusée lorsqu'aucun profil ciblé n'est détecté</span>
              </div>
            </div>
            {defaultAd ? (
              <div className="ai-card__table-wrapper">
                <table className="table table-striped projects mb-0">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Durée</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <DiffusionAiTargetingAdItem
                      aiTargetingAd={defaultAd}
                      totemId={totemSelected.id}
                      isDefaultAdRow={true}
                      maxDuration={totemSelected.team_totem?.duration}
                      onDelete={handleDeleteAd}
                      onUpdate={onAdUpdated}
                    />
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="ai-card__empty-state">
                <i className="fas fa-star ai-card__empty-state-icon ai-card__empty-state-icon--amber"></i>
                <p className="ai-card__empty-state-text">
                  Aucune ressource par défaut.<br />
                  Ajoutez-en une depuis la liste des ressources.
                </p>
              </div>
            )}
          </div>

          <div className="ai-card__section">
            <div className="ai-section-header ai-section-header--targeted">
              <div className="ai-section-header__icon-badge ai-section-header__icon-badge--blue">
                <i className="fas fa-crosshairs"></i>
              </div>
              <div className="ai-section-header__text">
                <span className="ai-section-header__title">
                  Ressources ciblées
                  {targetedAds.length > 0 && (
                    <span className="ai-section-header__count">{targetedAds.length}</span>
                  )}
                </span>
                <span className="ai-section-header__subtitle">Diffusées selon le profil détecté par l'IA</span>
              </div>
            </div>
            {targetedAds.length > 0 ? (
              <div className="ai-card__table-wrapper">
                <table className="table table-striped projects mb-0">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Durée</th>
                      <th>Planification</th>
                      <th>Genre</th>
                      <th>Âge</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {targetedAds.map((ad) => (
                      <DiffusionAiTargetingAdItem
                        key={ad.id}
                        aiTargetingAd={ad}
                        totemId={totemSelected.id}
                        totemHasDefaultAd={!!defaultAd}
                        otherTargetingAds={otherByAdId[ad.adId]}
                        maxDuration={totemSelected.team_totem?.duration}
                        onDelete={handleDeleteAd}
                        onUpdate={onAdUpdated}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="ai-card__empty-state">
                <i className="fas fa-crosshairs ai-card__empty-state-icon ai-card__empty-state-icon--blue"></i>
                <p className="ai-card__empty-state-text">
                  Aucune ressource ciblée.<br />
                  Ajoutez des ressources avec des critères de ciblage.
                </p>
              </div>
            )}
          </div>

          {aiTargetingAds.length > 0 && (
            <div className="ai-card__section ai-card__section--last">
              <AiTargetingSummaryMatrix aiTargetingAds={aiTargetingAds} />
            </div>
          )}
        </LoadingCard>
      </div>
      <div className="card-footer">
        <button type="button" className="btn btn-default float-right" onClick={onBack}>
          <i className="fas fa-arrow-left mr-1"></i> Retour
        </button>
      </div>
    </div>
  )
}
